import React, { useEffect, useState } from 'react';
import { init, AuthType } from "@thoughtspot/visual-embed-sdk";
import { BrowserRouter } from "react-router-dom";
import RenderRouter from "./routes";
import ErrorPage from "./common/pages/error";
import { ConfigProvider } from "antd";
import enUS from 'antd/es/locale/en_US';
import esES from 'antd/locale/es_ES';
import ptBR from 'antd/locale/pt_BR';
import { GetConfig, GetLanguage, getCookie } from "./helpers/utils";
import './styles.css'
import 'antd/dist/reset.css';
import { IntlProvider } from 'react-intl';
import { localeConfig } from './locales';
import { useAppDispatch, useAppState } from './stores';
import { setUserItem } from './stores/user.store';
import { setTokenGlobal } from './data/api/request';
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { Mixpanel } from './services/mixpanel/mixpanel.service'
import { getThoughtSpotUsername } from './services/thoughtspot/thoughtspot.service';
import 'dayjs/locale/es';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/pt-br';

dayjs.extend(quarterOfYear);

let config = GetConfig();
const cookieAuth = atob(getCookie('aivo_auth_jwt') || '');
const auth = cookieAuth && JSON.parse(cookieAuth);

const embedThoughtSpot = Boolean(config?.runtimeFilters);

// Initialize embed configuration thoughtspot
const initThoughtSpot = (thoughtSpotUsername?: string) => {
  const rulesCssPLatform = {
    '[id="rd-tooltip-container"]': {
      "display": config.liveboardId ? "none" : "inherit"
    },
    '.attribute-filter-module__bulkFilterButton': {
      "display": "none"
    },
    '.attribute-filter-module__toggleAndIconContainer': {
      "display": "none !important"
    },
    '.table-viz-module__colHeader .button-module__buttonWrapper': {
      "display": "none !important"
    },
    '.ag-header-cell.ag-focus-managed': {
      'pointer-events': 'none',
      'cursor': 'default !important'
    },
    '.chip-base-module__chip.chip-base-module__filter.date-filter-preview-module__previewChip': {
      'width': '100%'
    },
    '.react-grid-item.pinboard-content-module__tile': {
      'min-height': '60px'
    },
    '.pinboard-note-tile-module__normalMode': {
      'padding': '5px 15px',
      'min-height': '65px'
    },
    '.kpi-module__heroDataContainer .kpi-module__hero': {
      'font-size': '20px'
    }
  }
  init({
    thoughtSpotHost: `${process.env.REACT_APP_THOUGHTSPOT_HOST}`,
    authType: AuthType.AuthServer,
    username: thoughtSpotUsername,
    callPrefetch: true,
    getAuthToken: async () => {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${auth.accessToken}`);
      myHeaders.append("X-Token", auth.xToken);

      const requestOptions = {
        method: 'POST',
        headers: myHeaders
      };

      const response = await fetch(`${process.env.REACT_APP_AUTH_URL_API}/api/v1/thoughtspot/token?username=${encodeURIComponent(thoughtSpotUsername ?? '')}`, requestOptions);

      if (!response.ok) {
        throw new Error(`[thoughtspot] the token could not be obtained, status ${response.status}`);
      }

      return response.json();
    },
    autoLogin: true,
    disableLoginRedirect: true,
    suppressNoCookieAccessAlert: true,
    customizations: {
      style: {
        customCSS: {
          rules_UNSTABLE: config.app == 'aivo-live' ? {} : rulesCssPLatform
        },
      },
    },
  });
}

if (config) {
  setTokenGlobal(auth.accessToken, auth.xToken);
  if (config.auth?.email) {
    // Mixpanel.identify('should be UUID user'); // should be UUID user
    Mixpanel.register({
      'Email': config.auth?.email,
      'Bot Name': decodeURIComponent(config.botName) || 'N/A',
      'Section': config.section || 'N/A',
    });
  }
  // Mixpanel.track('access-login');
}

type ThoughtSpotStatus = 'pending' | 'ready' | 'error';

/**
 * The ThoughtSpot session is opened with the email resolved by analytics-mapper,
 * so init() has to wait for it: the username sent to ThoughtSpot and the one the
 * token is issued for must be the same. The embeds are rendered once it is ready.
 */
const thoughtSpotReady: Promise<ThoughtSpotStatus> = embedThoughtSpot
  ? getThoughtSpotUsername(config?.auth?.email)
    .then(initThoughtSpot)
    .then((): ThoughtSpotStatus => 'ready')
    .catch((error): ThoughtSpotStatus => {
      // there is no session to open without that email, so the platform is told
      // about it instead of authenticating with an address ThoughtSpot rejects
      console.error('[thoughtspot] the embed could not be initialized', error);
      window.parent.postMessage({
        event_id: 'error-ts',
        state: `${error?.message || error}`
      }, '*');
      return 'error';
    })
  : Promise.resolve<ThoughtSpotStatus>('ready');

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { locale } = useAppState((state) => state.user);
  const [thoughtSpotStatus, setThoughtSpotStatus] = useState<ThoughtSpotStatus>(embedThoughtSpot ? 'pending' : 'ready');

  useEffect(() => {
    thoughtSpotReady.then(setThoughtSpotStatus);
  }, [])

  useEffect(() => {
    // currentUser keeps the platform email of the cookie, the one the chat report
    // needs. It is not the email ThoughtSpot is initialized with
    dispatch(
      setUserItem(config?.runtimeFilters ? {
        locale: GetLanguage(config?.locale),
        configThoughtSpot: config,
        currentUser: {
          email: config?.auth?.email
        }
      } : {
        locale: GetLanguage(config?.locale),
        currentUser: {
          email: config?.auth?.email
        }
      }),
    );
  }, [])

  useEffect(() => {
    switch (locale) {
      case 'en_US':
        dayjs.locale('en');
        break;
      case 'pt_BR':
        dayjs.locale('pt');
        break;
      default:
        dayjs.locale('es');
        break;
    }
  }, [locale]);


  const getAntdLocale = () => {
    switch (locale) {
      case 'en_US':
        return enUS;
      case 'pt_BR':
        return ptBR;
      default:
        return esES;
    }
  };

  return (
    <ConfigProvider
      locale={getAntdLocale()}
      theme={{
        token: {
          colorPrimary: '#6579FF',
        },
      }}
    >
      <IntlProvider locale={locale.split('_')[0]} messages={localeConfig[locale]}>
        {thoughtSpotStatus === 'error'
          ? <ErrorPage />
          : (
            <BrowserRouter>
              {thoughtSpotStatus === 'ready' && <RenderRouter />}
            </BrowserRouter>
          )}
      </IntlProvider>
    </ConfigProvider>
  );
};

export default App;
